import type { Node } from '../../../core/models/scene';

/**
 * Lane containment helper.
 *
 * Swim lanes in this codebase are pool tables (`attrs.__isPoolTable`)
 * with `attrs.laneBounds = [{start, size}, ...]` defining each band's
 * spatial bounds and `attrs.cellText` holding the lane labels.
 *
 * The hydrator stamps `attrs.__laneLabel` on each step at RDM-load
 * time, but a step that's *dragged* into a lane after the fact never
 * gets that update — there's no runtime hook into the move command.
 * This helper closes the gap by computing containment lazily from
 * the current geometry whenever a consumer (property panel caption,
 * lens chip, owner fallback) needs to know what lane a node lives in.
 *
 * Two-tier lookup:
 *   1. Trust `attrs.__laneLabel` if it's already there (the cheap
 *      and consistent path for hydrated diagrams).
 *   2. Otherwise locate the pool table, infer lane direction from
 *      the cellText layout, and find the lane whose band contains
 *      the node's centre. Returns the lane's label (or "Lane N+1"
 *      as a defensive fallback when the cellText slot is empty).
 *
 * Returns `undefined` when there's no pool table or the node sits
 * outside every lane band — callers should treat that as "no lane".
 */
interface LaneResolution {
  index: number;
  label: string;
  direction: 'horizontal' | 'vertical';
}

const SWIMLANE_SCHEMAS = new Set(['swimlane-horizontal', 'swimlane-vertical']);

/**
 * Find the swimlane pool table in the given node set. Recognises both
 * RDM-hydrated tables (carry `attrs.__isPoolTable === true`, set in
 * `RdmHydrator`) and editor-created tables dropped from the shape
 * picker (carry `attrs.schemaId` of `swimlane-horizontal` or
 * `swimlane-vertical`, see `tools/universal/ShapePickerTool.tsx`).
 *
 * Exported so the editor's Tidy command can locate the same table the
 * containment helper uses, regardless of how the swim lanes were
 * created.
 */
export function findPoolTable(allNodes: readonly Node[]): Node | undefined {
  return findPoolTables(allNodes)[0];
}

export function findPoolTables(allNodes: readonly Node[]): Node[] {
  return allNodes.filter((n) => {
    const a = (n.attrs ?? {}) as Record<string, unknown>;
    if (a.__isPoolTable === true) return true;
    const schemaId = typeof a.schemaId === 'string' ? a.schemaId : undefined;
    if (schemaId && SWIMLANE_SCHEMAS.has(schemaId)) return true;
    return false;
  });
}

export function findContainingPool(node: Node, allNodes: readonly Node[]): Node | undefined {
  const centerX = (node.x ?? 0) + (node.w ?? 100) / 2;
  const centerY = (node.y ?? 0) + (node.h ?? 50) / 2;
  return findPoolTables(allNodes)
    .filter((pool) => {
      const x = pool.x ?? 0;
      const y = pool.y ?? 0;
      const w = pool.w ?? (pool as { width?: number }).width ?? 0;
      const h = pool.h ?? (pool as { height?: number }).height ?? 0;
      return centerX >= x && centerX <= x + w && centerY >= y && centerY <= y + h;
    })
    .sort((a, b) => {
      const area = (pool: Node) =>
        (pool.w ?? (pool as { width?: number }).width ?? 0) *
        (pool.h ?? (pool as { height?: number }).height ?? 0);
      return area(a) - area(b);
    })[0];
}

/**
 * Compute lane bands from a pool table's `attrs.laneBounds` if the
 * field exists (RDM-hydrated or post-layout), else fall back to the
 * raw `rowHeights` / `columnWidths` the editor's table primitive
 * stores from the shape's schema. Editor-created swimlanes don't
 * have `laneBounds` until a layout pass populates it.
 */
export function getPoolLaneDirection(pool: Node): 'horizontal' | 'vertical' {
  const poolAttrs = (pool.attrs ?? {}) as Record<string, unknown>;
  const cellText = (poolAttrs.cellText ?? {}) as Record<string, string>;
  const schemaId = typeof poolAttrs.schemaId === 'string' ? poolAttrs.schemaId : undefined;
  if (schemaId === 'swimlane-vertical') return 'vertical';
  if (schemaId === 'swimlane-horizontal') return 'horizontal';
  const keys = Object.keys(cellText);
  const horizontalHits = keys.filter((k) => k.endsWith(',0')).length;
  const verticalHits = keys.filter((k) => k.startsWith('0,')).length;
  return verticalHits > horizontalHits ? 'vertical' : 'horizontal';
}

export function readPoolLaneBounds(
  pool: Node,
  direction: 'horizontal' | 'vertical',
): Array<{ start: number; size: number }> | undefined {
  const poolAttrs = (pool.attrs ?? {}) as Record<string, unknown>;
  const stored = poolAttrs.laneBounds as Array<{ start: number; size: number }> | undefined;
  if (Array.isArray(stored) && stored.length > 0) return stored;

  // Fallback: derive bands from row heights (horizontal lanes) or
  // column widths (vertical lanes). The header track sits in col 0
  // (horizontal) or row 0 (vertical) and isn't a lane.
  const sizes = direction === 'horizontal'
    ? (poolAttrs.rowHeights as number[] | undefined)
    : (poolAttrs.columnWidths as number[] | undefined);
  if (!Array.isArray(sizes) || sizes.length === 0) return undefined;

  const bounds: Array<{ start: number; size: number }> = [];
  let cursor = 0;
  for (const size of sizes) {
    bounds.push({ start: cursor, size });
    cursor += size;
  }
  return bounds;
}

/**
 * Internal: full lane resolution from geometry. Used by both the label
 * lookup (cosmetic surface) and the index lookup (RDM-style swimlane
 * Tidy, which needs the index to call into RdmLayoutService.applyLayout).
 */
function resolveLaneFromGeometry(node: Node, allNodes: readonly Node[]): LaneResolution | undefined {
  const pool = findPoolTable(allNodes);
  if (!pool) return undefined;

  const poolAttrs = (pool.attrs ?? {}) as Record<string, unknown>;
  const cellText = (poolAttrs.cellText ?? {}) as Record<string, string>;

  const direction = getPoolLaneDirection(pool);
  const laneBounds = readPoolLaneBounds(pool, direction);
  if (!laneBounds || laneBounds.length === 0) return undefined;

  const nodeCenter = (() => {
    const x = ((node as { x?: number }).x ?? 0) + ((node as { w?: number }).w ?? 100) / 2;
    const y = ((node as { y?: number }).y ?? 0) + ((node as { h?: number }).h ?? 50) / 2;
    return { x, y };
  })();
  const poolPos = {
    x: ((pool as { x?: number }).x ?? 0),
    y: ((pool as { y?: number }).y ?? 0),
  };

  const localPos = direction === 'horizontal'
    ? nodeCenter.y - poolPos.y
    : nodeCenter.x - poolPos.x;

  let foundIndex = -1;
  for (let i = 0; i < laneBounds.length; i++) {
    const band = laneBounds[i]!;
    if (localPos >= band.start && localPos < band.start + band.size) {
      foundIndex = i;
      break;
    }
  }
  if (foundIndex < 0) return undefined;

  const cellKey = direction === 'horizontal' ? `${foundIndex},0` : `0,${foundIndex}`;
  const cell = cellText[cellKey];
  const label = typeof cell === 'string' && cell.length > 0 ? cell : `Lane ${foundIndex + 1}`;
  return { index: foundIndex, label, direction };
}

export function getLaneLabelForNode(node: Node, allNodes: readonly Node[]): string | undefined {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const cached = attrs.__laneLabel;
  if (typeof cached === 'string' && cached.length > 0) return cached;
  return resolveLaneFromGeometry(node, allNodes)?.label;
}

/**
 * Returns the lane band index a node currently overlaps (0-based).
 *
 * Trusts `attrs.__laneIndex` if it's already there (RDM hydration sets
 * this); otherwise computes it from pool-table geometry. Used by the
 * editor's swimlane-aware Tidy command to seed `__laneIndex` on clones
 * before delegating to `RdmLayoutService.applyLayout`, which groups
 * nodes by that key.
 *
 * Returns `undefined` when there's no pool table or the node sits
 * outside every lane band — caller should treat that as "no lane".
 */
export function getLaneIndexForNode(node: Node, allNodes: readonly Node[]): number | undefined {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const cached = attrs.__laneIndex;
  if (typeof cached === 'number' && Number.isFinite(cached)) return cached;
  return resolveLaneFromGeometry(node, allNodes)?.index;
}
